import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { markLectureAsComplete } from '../../../services/operations/courseDetailsAPI';
import { updateCompletedLectures } from '../../../slices/viewCourseSlice';
import { BigPlayButton, ControlBar, PlaybackRateMenuButton, Player } from 'video-react';
import "video-react/dist/video-react.css";
import IconBtn from '../../common/IconBtn';

const VideoDetails = () => {

    const { courseId, sectionId, subSectionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const playerRef = useRef(null);
    const dispatch = useDispatch();
    const { token } = useSelector((state) => state.auth);
    const { courseSectionData, courseEntireData, completedLectures } = useSelector((state) => state.viewCourse);

    const [videoData, setVideoData] = useState([]);
    const [previewSource, setPreviewSource] = useState("");
    const [videoEnded, setVideoEnded] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(()=>{
        const setVideoSpecificDetails = async () =>{
            if(!courseSectionData.length){
                return;
            }
            if (!courseId && !sectionId && !subSectionId) {
                navigate(`/dashboard/enrolled-courses`);
            } else{
                const filteredData = courseSectionData.filter( (section) => section._id === sectionId);
                const filteredVideoData = filteredData?.[0]?.subSection.filter((subSec) => subSec._id === subSectionId);
                setVideoData(filteredVideoData[0]);
                setVideoEnded(false);
                setPreviewSource(courseEntireData.thumbnail);
            }
        }
        setVideoSpecificDetails();
    }, [courseSectionData, courseEntireData, location.pathname]);

    const isFirstVideo = () =>{
        const currentSectionIndx = courseSectionData.findIndex((section) => section._id === sectionId);
        const currentSubSectionIndx = courseSectionData[currentSectionIndx].subSection.findIndex((subSec) => subSec._id === subSectionId);

        if(currentSectionIndx === 0 && currentSubSectionIndx === 0){
            return true;
        }else{
            return false;
        }
    }

    const isLastVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex((section) => section._id === sectionId);
        const currentSubSectionIndx = courseSectionData[currentSectionIndx].subSection.findIndex((subSec) => subSec._id === subSectionId);
        const noOfSubsections = courseSectionData[currentSectionIndx].subSection.length;

        if(currentSectionIndx === courseSectionData.length - 1 && currentSubSectionIndx === noOfSubsections - 1){
            return true;
        }else{
            return false;
        }
    }

    const goToNextVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex((section) => section._id === sectionId);
        const currentSubSectionIndx = courseSectionData[currentSectionIndx].subSection.findIndex((subSec) => subSec._id === subSectionId);
        const noOfSubsections = courseSectionData[currentSectionIndx].subSection.length;

        if(currentSubSectionIndx !== noOfSubsections-1){
            const nextSubSectionId = courseSectionData[currentSectionIndx].subSection[currentSubSectionIndx + 1]._id;
            navigate(`/view-course/${courseId}/section/${sectionId}/sub-section/${nextSubSectionId}`);
        }else{
            const nextSectionId = courseSectionData[currentSectionIndx + 1]._id;
            const nextSubSectionId = courseSectionData[currentSectionIndx + 1].subSection[0]._id;
            navigate(`/view-course/${courseId}/section/${nextSectionId}/sub-section/${nextSubSectionId}`);
        }
    }

    const goToPrevVideo = () => {
        const currentSectionIndx = courseSectionData.findIndex((section) => section._id === sectionId);
        const currentSubSectionIndx = courseSectionData[currentSectionIndx].subSection.findIndex((subSec) => subSec._id === subSectionId);
        
        if(currentSubSectionIndx !== 0){
            const prevSubSectionId = courseSectionData[currentSectionIndx].subSection[currentSubSectionIndx - 1]._id;
            navigate(`/view-course/${courseId}/section/${sectionId}/sub-section/${prevSubSectionId}`);
        }else{
            const prevSectionId = courseSectionData[currentSectionIndx - 1]._id;
            const prevSubSectionLength = courseSectionData[currentSectionIndx - 1].subSection.length;
            const prevSubSectionId = courseSectionData[currentSectionIndx - 1].subSection[prevSubSectionLength - 1]._id;
            navigate(`/view-course/${courseId}/section/${prevSectionId}/sub-section/${prevSubSectionId}`);
        }
    }

    const handleLectureCompletion = async () => {
        setLoading(true);
        const res = await markLectureAsComplete({courseId: courseId, subSectionId: subSectionId}, token);
        if(res){
            dispatch(updateCompletedLectures(subSectionId));
        }
        setLoading(false);
        if(!isLastVideo()){
            goToNextVideo();
        }
    }

    return (
        <div className='flex flex-col gap-5 text-white'>
            {
                !videoData ? (
                    <img src={previewSource} alt="preview" className="h-full w-full rounded-md object-cover" />
                ) : (
                    <Player aspectRatio='16:9' playsInline ref={playerRef}
                        onEnded={()=> setVideoEnded(true)} 
                        src={videoData.videoUrl}
                    >
                        <BigPlayButton position='center'/>
                        <ControlBar>
                            <PlaybackRateMenuButton/>
                        </ControlBar>

                        {/* render when video ends */}
                        {
                            videoEnded && (
                                <div className="full absolute inset-0 z-[100] grid h-full place-content-center font-inter"
                                    style={{background:"linear-gradient(to top, rgb(0, 0, 0), rgba(0,0,0,0.7), rgba(0,0,0,0.5), rgba(0,0,0,0.1)"}}
                                >
                                    {
                                        !completedLectures.includes(subSectionId) && (
                                            <IconBtn disabled={loading} onClick={() => handleLectureCompletion()}
                                                text={!loading ? "Mark As Completed" : "Loading..."}
                                                customClasses="text-xl max-w-max px-4 mx-auto"
                                            />
                                        )
                                    }
                                    <IconBtn disabled={loading}
                                        onClick={() => {
                                            if (playerRef?.current) {
                                                playerRef?.current?.seek(0);
                                                playerRef?.current.play();
                                                setVideoEnded(false);
                                            }
                                        }}
                                        text="Rewatch"
                                        customClasses="text-xl max-w-max px-4 mx-auto mt-2"
                                    />

                                    <div className="mt-10 flex min-w-[250px] justify-center gap-x-4 text-xl">
                                        {
                                            !isFirstVideo() && (
                                                <button disabled={loading} onClick={goToPrevVideo} className='blackButton'>
                                                    Previous
                                                </button>
                                            )
                                        }
                                        {
                                            !isLastVideo() && (
                                                <button disabled={loading} onClick={goToNextVideo} className='blackButton'>
                                                    Next
                                                </button>
                                            )
                                        }
                                    </div>
                                </div>
                            )
                        }
                    </Player>
                )
            }

            <h1 className="mt-4 text-3xl font-semibold">{videoData?.title}</h1>
            <p className="pt-2 pb-6">{videoData?.description}</p>
        </div>
    )
}

export default VideoDetails
